import { createTheme, type Theme } from '@mui/material/styles'
import { enUS as coreEnUS, ptPT as corePtPT } from '@mui/material/locale'
import { enUS as pickersEnUS, ptPT as pickersPtPT } from '@mui/x-date-pickers/locales'
import type {} from '@mui/x-date-pickers/themeAugmentation'
import { brand, fontFamily, radius, status, surface, text, TOUCH_TARGET } from './tokens'

export * from './tokens'

// Cor única para texto secundário/desactivado (legendas, placeholders,
// campos só-leitura). Substitui os defaults da MUI — `text.disabled`
// (rgba(0,0,0,0.38), ~2.7:1) e `action.disabled` (rgba(0,0,0,0.26), ~1.9:1)
// — que falham o mínimo AA (4.5:1). Este tom dá >6:1 sobre branco e sobre
// os cinzas claros já usados em campos/botões desactivados.
export const MUTED_TEXT_COLOR = '#4B5563'
export const DISABLED_BACKGROUND = '#E5E7EB'

declare module '@mui/material/styles' {
  interface Palette {
    brand: typeof brand
    surface: typeof surface
    status: typeof status
  }
  interface PaletteOptions {
    brand?: typeof brand
    surface?: typeof surface
    status?: typeof status
  }
}

/** Ponteiro grosso (dedo) — aqui os alvos de toque crescem para 44 px. */
const COARSE = '@media (pointer: coarse)'

export type ThemeLanguage = 'pt' | 'en'

export function buildTheme(lang: ThemeLanguage = 'pt'): Theme {
  const base = createTheme({
    palette: {
      primary: { main: brand.navy900, light: brand.navy700, dark: '#071A33', contrastText: '#ffffff' },
      // Turquesa escuro como "secondary": é o tom AA para texto/links; o turquesa
      // claro (teal500) fica em `brand` para gráficos e ícones activos.
      secondary: { main: brand.teal700, light: brand.teal500, dark: '#08545C', contrastText: '#ffffff' },
      success: { main: status.success.fg, contrastText: '#ffffff' },
      warning: { main: status.warning.fg, contrastText: '#ffffff' },
      error: { main: status.danger.fg, contrastText: '#ffffff' },
      info: { main: status.info.fg, contrastText: '#ffffff' },
      background: { default: surface.app, paper: surface.paper },
      text: { primary: text.primary, secondary: text.secondary, disabled: MUTED_TEXT_COLOR },
      divider: surface.border,
      action: { disabled: MUTED_TEXT_COLOR, disabledBackground: DISABLED_BACKGROUND },
      brand,
      surface,
      status,
    },
    typography: {
      fontFamily,
      // Escala do briefing: título de página 28/800, título de cartão 16/800,
      // corpo 14/500, legenda 12/600 — nunca abaixo de 12 px.
      h1: { fontSize: '1.75rem', fontWeight: 800, lineHeight: 1.25, letterSpacing: '-0.01em' },
      h2: { fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.3 },
      h3: { fontSize: '1.375rem', fontWeight: 800, lineHeight: 1.3 },
      h4: { fontSize: '1.75rem', fontWeight: 800, lineHeight: 1.25, letterSpacing: '-0.01em' },
      h5: { fontSize: '1.25rem', fontWeight: 800, lineHeight: 1.3 },
      h6: { fontSize: '1rem', fontWeight: 800, lineHeight: 1.4 },
      subtitle1: { fontSize: '1rem', fontWeight: 700 },
      subtitle2: { fontSize: '0.875rem', fontWeight: 700 },
      body1: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.55 },
      body2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5 },
      caption: { fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.45 },
      overline: { fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.06em', lineHeight: 1.5 },
      button: { fontSize: '0.875rem', fontWeight: 700, textTransform: 'none' },
    },
    // Unidade de raio = 8 px: `sx={{ borderRadius: 2 }}` (o padrão dos cartões
    // em toda a app) dá os 16 px do briefing; botões e inputs fixam 12 px abaixo.
    shape: { borderRadius: 8 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: surface.app,
            // Remove o flash cinza nativo do toque em iOS/Android — o feedback
            // táctil passa a vir só do `:active` controlado no MuiButtonBase.
            WebkitTapHighlightColor: 'transparent',
          },
        },
      },
      // Base partilhada por Button, IconButton, ListItemButton, CardActionArea,
      // ToggleButton, etc. — um único ponto de feedback táctil (`brightness`
      // escurece sempre, seja qual for o fundo) e um anel de foco visível só
      // para quem navega por teclado.
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '&:active': { filter: 'brightness(0.92)' },
            '&.Mui-focusVisible': { outline: `2px solid ${brand.teal500}`, outlineOffset: 2 },
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: radius.control,
            minHeight: TOUCH_TARGET,
            padding: '10px 20px',
            lineHeight: 1.4,
          },
          sizeSmall: {
            minHeight: 36,
            padding: '6px 14px',
            [COARSE]: { minHeight: TOUCH_TARGET },
          },
          sizeLarge: { minHeight: 48, padding: '12px 24px', fontSize: '0.9375rem' },
          outlined: { borderColor: surface.borderInput },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': { cursor: 'not-allowed' },
            [COARSE]: { minWidth: TOUCH_TARGET, minHeight: TOUCH_TARGET },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 700, [COARSE]: { minHeight: TOUCH_TARGET } },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 700, minHeight: TOUCH_TARGET },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'medium' },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.control,
            backgroundColor: surface.paper,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: surface.borderInput },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: brand.navy900 },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: brand.navy900, borderWidth: '1.5px' },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: status.danger.fg },
            // Aspecto de "só leitura" para campos desactivados (ex: registos
            // que já não são rascunho) — o fundo branco/borda normal por
            // omissão fazia-os parecer editáveis apesar do `disabled`.
            '&.Mui-disabled': {
              backgroundColor: surface.app,
              cursor: 'not-allowed',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: surface.border },
            },
          },
          multiline: { padding: '11px 14px' },
          // Sem recorte para o rótulo (que agora está por cima do campo).
          notchedOutline: { top: 0, '& legend': { display: 'none' } },
          input: {
            // 44 px de altura com o texto de 14 px (16 px em ecrãs tácteis).
            padding: '11px 14px',
            '&.MuiInputBase-inputSizeSmall': { padding: '8px 12px' },
            // Em multilinha o padding é do contentor (abaixo) — sem isto ficava a dobrar.
            '&.MuiInputBase-inputMultiline': { padding: 0 },
            '&.Mui-disabled': { color: MUTED_TEXT_COLOR, WebkitTextFillColor: MUTED_TEXT_COLOR, cursor: 'not-allowed' },
          },
        },
      },
      // O Autocomplete tem padding próprio — alinhado com os 44 px dos restantes campos.
      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: {
            '&.MuiOutlinedInput-root:not(.MuiInputBase-sizeSmall)': { paddingTop: 4, paddingBottom: 4 },
          },
        },
      },
      // Os campos de data do MUI X (DateField/DateTimeField/TimeField) usam o seu
      // próprio input — o mesmo aspecto dos restantes.
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.control,
            backgroundColor: surface.paper,
            '& .MuiPickersOutlinedInput-notchedOutline': { borderColor: surface.borderInput, top: 0, '& legend': { display: 'none' } },
            '& .MuiPickersInputBase-sectionsContainer': { paddingTop: 11, paddingBottom: 11 },
            '&.MuiPickersInputBase-inputSizeSmall .MuiPickersInputBase-sectionsContainer': { paddingTop: 8, paddingBottom: 8 },
            '&:hover .MuiPickersOutlinedInput-notchedOutline': { borderColor: brand.navy900 },
            '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': { borderColor: brand.navy900, borderWidth: '1.5px' },
            '&.Mui-disabled': { backgroundColor: surface.app, cursor: 'not-allowed' },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            '&.Mui-disabled': { color: MUTED_TEXT_COLOR, WebkitTextFillColor: MUTED_TEXT_COLOR, cursor: 'not-allowed' },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            // O iOS amplia a página ao focar inputs com texto < 16 px.
            [COARSE]: { fontSize: '1rem' },
            // O placeholder nativo do browser usa opacidade variável (e falha
            // AA em Chrome/Edge, ~3:1) — fixa-se numa cor sólida consistente.
            '&::placeholder': { color: MUTED_TEXT_COLOR, opacity: 1 },
          },
        },
      },
      // Rótulo por cima do campo (não flutuante dentro da borda) e ajuda por
      // baixo — briefing 4.1. O rótulo fica estático e o recorte (legend) da
      // borda desaparece; em todos os TextField/Select/Autocomplete/pickers.
      MuiInputLabel: {
        defaultProps: { shrink: true },
        styleOverrides: {
          root: {
            position: 'relative',
            transform: 'none',
            maxWidth: '100%',
            whiteSpace: 'normal',
            overflow: 'visible',
            marginBottom: 6,
            fontSize: '0.8125rem',
            fontWeight: 700,
            lineHeight: 1.4,
            color: text.primary,
            pointerEvents: 'auto',
            '&.Mui-focused': { color: brand.navy900 },
            '&.Mui-error': { color: status.danger.fg },
            '&.Mui-disabled': { color: MUTED_TEXT_COLOR },
            '& .MuiFormLabel-asterisk': { color: status.danger.fg },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: { root: { marginLeft: 0, marginRight: 0, marginTop: 6, fontSize: '0.75rem', fontWeight: 500 } },
      },
      MuiPaper: {
        styleOverrides: {
          // Separação por bordas, não por sombras (briefing 4.1): a elevação 1
          // (cartões por omissão) fica plana; menus e diálogos mantêm a sua.
          elevation1: { boxShadow: 'none', border: `1px solid ${surface.border}` },
        },
      },
      MuiCard: {
        styleOverrides: { root: { borderRadius: radius.card } },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: radius.card },
          // Formulários longos (diálogos `fullWidth`) ocupam o ecrã inteiro no
          // telemóvel — sem margens perdidas nem scroll duplo.
          paperFullWidth: ({ theme }) => ({
            [theme.breakpoints.down('sm')]: {
              margin: 0,
              width: '100%',
              maxWidth: '100%',
              maxHeight: '100%',
              minHeight: '100%',
              borderRadius: 0,
            },
          }),
        },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: radius.control } },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: radius.chip },
          sizeSmall: { height: 24, fontSize: '0.75rem' },
        },
      },
      MuiTooltip: {
        styleOverrides: { tooltip: { fontSize: '0.75rem', fontWeight: 600 } },
      },
      // Cabeçalho uniforme para todas as tabelas: fundo subtil, texto secundário a negrito,
      // com a mesma letra do corpo (body2). As versaletes de antes desenhavam as letras à
      // altura das minúsculas e o cabeçalho lia-se mais pequeno do que as linhas.
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-root': {
              backgroundColor: surface.subtle,
              color: text.secondary,
              fontWeight: 700,
              fontSize: '0.875rem',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottomColor: surface.border, fontVariantNumeric: 'tabular-nums' },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            '&.Mui-active': { color: brand.navy900 },
            '&.Mui-completed': { color: brand.teal700 },
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: { '&.Mui-active': { fontWeight: 700, color: brand.navy900 } },
        },
      },
    },
  })

  // Textos internos dos componentes (paginação, "No options", pickers) na
  // língua da interface.
  return lang === 'en'
    ? createTheme(base, coreEnUS, pickersEnUS)
    : createTheme(base, corePtPT, pickersPtPT)
}

const theme = buildTheme('pt')

export default theme
